import axios from "axios";
import { API_ROUTE } from "./Constants";

/**
 * Attaches the `CloudCoverToken` auth header (read from sessionStorage) to every
 * outgoing request that targets our own API.
 *
 * Background: the token used to be added by hand in each axios call, so some calls
 * omitted it. Now that several API endpoints enforce `CloudCoverToken`, a missing
 * header returns 401. Registering this interceptor once (on the global axios
 * singleton at app startup, and on the shared `api` instance below) guarantees the
 * token is always sent, so no call can forget it.
 *
 * Scope: `CloudCoverToken` only. The Microsoft Graph `Authorisation` token is left
 * to the callers to set (it is only obtainable via an async MSAL call, and none of
 * the secured endpoints check it).
 *
 * Safety: the token is only attached to internal requests (relative URLs, or URLs
 * that target API_ROUTE) so it can never leak to a third-party host.
 */
export function attachCloudCoverToken(instance) {
    instance.interceptors.request.use((config) => {
        const url = config.url || "";
        // Attach only to our own API. Internal = a URL under API_ROUTE, or a plain relative path
        // (NOT protocol-relative "//host" and NOT another scheme) so the token can never leak to a
        // third-party/external host.
        const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
        const isProtocolRelative = url.startsWith("//");
        const isInternal = url.startsWith(API_ROUTE) || (!hasScheme && !isProtocolRelative);

        if (isInternal) {
            const cloudCoverToken = sessionStorage.getItem("CloudCoverToken");
            if (cloudCoverToken) {
                // axios v1 uses an AxiosHeaders instance (has .set); fall back to a
                // plain object merge for safety. Only this one header is touched, so
                // any other headers, responseType and request body are preserved.
                if (config.headers && typeof config.headers.set === "function") {
                    config.headers.set("CloudCoverToken", cloudCoverToken);
                } else {
                    config.headers = { ...(config.headers || {}), CloudCoverToken: cloudCoverToken };
                }
            }
        }

        return config;
    });

    return instance;
}

/**
 * Shared axios instance and go-forward standard for new/refactored code.
 * Usage: `import api from "../Helpers/apiClient"; api.get(Routes.X);`
 * (baseURL is API_ROUTE, so pass the relative route only).
 */
const api = axios.create({ baseURL: API_ROUTE });
attachCloudCoverToken(api);

export default api;
